import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { ConfirmProvider, ToastProvider } from "@plerity/ui-core";
import { Bell, Bot, LayoutDashboard, MapPin, Megaphone, Settings, Star, Trophy } from "lucide-react";
import { BusinessSwitcher } from "./components/BusinessSwitcher";
import { AppSettingsTabContent } from "./routes/settings/SettingsTabContent";

const sidebarGroups: SidebarGroup[] = [
  {
    title: "Reviews",
    defaultOpen: true,
    routes: [
      {
        path: "overview",
        label: "Overview",
        icon: LayoutDashboard,
        loader: () => import("./routes/Overview"),
      },
      {
        path: "reviews",
        label: "All Reviews",
        icon: Star,
        loader: () => import("./routes/ReviewList"),
      },
      {
        path: "reviews/:id",
        label: "Review Detail",
        icon: Star,
        loader: () => import("./routes/ReviewDetail"),
        hidden: true,
      },
      {
        path: "ai-responses",
        label: "AI Responses",
        icon: Bot,
        loader: () => import("./routes/AIResponses"),
      },
      {
        path: "campaigns",
        label: "Campaigns",
        icon: Megaphone,
        loader: () => import("./routes/Campaigns"),
      },
      {
        path: "campaigns/:id",
        label: "Campaign Detail",
        icon: Megaphone,
        loader: () => import("./routes/CampaignDetail"),
        hidden: true,
      },
      {
        path: "competitors",
        label: "Competitors",
        icon: Trophy,
        loader: () => import("./routes/Competitors"),
      },
      {
        path: "alerts",
        label: "Alerts",
        icon: Bell,
        loader: () => import("./routes/Alerts"),
      },
    ],
  },
  {
    title: "Configuration",
    routes: [
      {
        path: "locations",
        label: "Locations",
        icon: MapPin,
        loader: () => import("./routes/Locations"),
      },
    ],
  },
  {
    // Auth routes — hidden from sidebar, full-screen redirect pages
    title: "Auth",
    routes: [
      {
        path: "auth/login",
        label: "Login",
        icon: Star,
        loader: () => import("./routes/auth/Login"),
        hidden: true,
      },
      {
        path: "auth/callback",
        label: "Callback",
        icon: Star,
        loader: () => import("./routes/auth/Callback"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/reviews";

// Reviews-specific settings tabs injected into the unified UserSettingsPage.
// Reached via top-right avatar → Settings (auto-mounted at /settings by createShell).
// Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
const settingsTabs: SettingsTab[] = [
  {
    id: "reviews-settings",
    label: "Reviews Settings",
    icon: Settings,
    content: <AppSettingsTabContent />,
  },
];

// "Powered by Plerity" sidebar footer — visible to all tenants.
// a11y 1.4.1: the link itself uses `text-fg` (passes AA against the
// sidebar background in both themes) and a hairline underline so it remains
// identifiable as a link without relying on color alone. The surrounding
// "Powered by " label stays in the muted treatment.
// Backlog: visual-reviews-poweredby-link-contrast-2026-05-24-001
const PoweredByFooter = (
  <div className="border-t px-4 py-3">
    <p className="text-xs text-muted">
      Powered by{" "}
      <a
        href="https://animasmarketing.com"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-fg underline underline-offset-2 decoration-muted hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-sm"
      >
        Plerity
      </a>
    </p>
  </div>
);

const Shell = createShell({
  name: "reviews",
  displayName: "Reviews",
  sidebarGroups,
  defaultRoute: "/overview",
  currentApp: "reviews",
  basename,
  sidebarFooter: PoweredByFooter,
  // Header business switcher — scope every page to one managed business.
  // Hidden automatically when only one business exists (see BusinessSwitcher).
  headerActions: <BusinessSwitcher />,
  settingsRoute: "/settings",
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  auth: {
    required: true,
    entitlement: "reviews",
  },
  chat: {
    enabled: true,
    features: [
      "reviews",
      "locations",
      "review-requests",
      "campaigns",
      "templates",
      "competitors",
      "ai-responses",
      "widget",
      "alerts",
    ],
  },
  testerNotes: {
    enabled: true,
    contextId: "web-reviews",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes / Assistant / Privacy
  // are auto-included by UserSettingsPage; we inject the Reviews-specific tab here.
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22 (Phase 2d).
  settings: {
    additionalTabs: settingsTabs,
    assistantName: "Reviews Assistant",
  },
});

function App() {
  return (
    <ToastProvider position="bottom-right">
      <ConfirmProvider>
        <Shell />
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
